import { Matrix, namespace, Point } from '../../../../../FrontendBundle/Resources/js/ofcs';
import GroupItem from './groupItem';

namespace('Ofcs.Editor.Item');

/**
 * @class TabletopGroupItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.TabletopGroupItem = GroupItem.extend({

    /**
     * Item type
     *
     * @property {string} type
     */
    type: 'tabletopGroup',

    /**
     * Center point of group at the time the group is selected.
     * Used to rotate around the same center point in several steps.
     *
     * @property {Point} rotationCenter
     */
    rotationCenter: null,

    defaults: {
        /**
         * @property {String} Type
         */
        type: 'TabletopGroup',

        /**
         * Name of the item
         * @property {string} attributes.name
         */
        name: null,

        /**
         * Current rotation
         * @property {number} attributes.rotation
         */
        rotation: 0,

        /**
         * Extended path coordinates
         * @property {Array} attributes.ext
         */
        ext: [],

        /**
         * Original path coordinates
         * @property {Array} attributes.orig
         */
        orig: [],

        /**
         * Safety margin path coordinates
         * @property {Array} attributes.safety
         */
        safety: [],
    },

    /**
     * Array with grouped items
     *
     * @property {Array} items
     */

    /**
     * Initializes a new group item
     *
     * @constructor
     */
    initialize() {
        GroupItem.prototype.initialize.call(this);

        this.supportsRotation = false;
        this.supportsBidirectionalCollisions = true;
        this.supportsInsideTable = 'edge';
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTop(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.getHeight(withMargin);
        const point = new Point(center.x, center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottom(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.getHeight(withMargin);
        const point = new Point(center.x, center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const point = new Point(center.x - (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const point = new Point(center.x + (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const height = this.getHeight(withMargin);
        const point = new Point(center.x - (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointTopRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const height = this.getHeight(withMargin);
        const point = new Point(center.x + (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomLeft(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const height = this.getHeight(withMargin);
        const point = new Point(center.x - (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @param {boolean} withMargin - if true will include safetyMargins
     * @return {Point}
     */
    getPointBottomRight(withMargin) {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.getWidth(withMargin);
        const height = this.getHeight(withMargin);
        const point = new Point(center.x + (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get width of group
     *
     * @param {boolean} withMargin - if true will include margins
     * @return {number}
     */
    getWidth(withMargin) {
        const bounds = withMargin ? this.getMarginBounds() : this.getBounds();

        const width = this.cache.get('width', () => bounds.bottomRight.x - bounds.topLeft.x);

        return width;
    },

    /**
     * Get height of group
     *
     * @param {boolean} withMargin - if true will include margins
     * @return {number}
     */
    getHeight(withMargin) {
        const bounds = withMargin ? this.getMarginBounds() : this.getBounds();

        const height = this.cache.get('height', () => bounds.bottomRight.y - bounds.topLeft.y);

        return height;
    },
});
